/**
 * Adaptador shape v1↔v2 para `structured_table`.
 *
 * Tanto el `SectionEditor` (modo edición runtime) como el
 * `SectionRenderer` (viewer read-only) leen el schema de columnas desde
 * `meta.columns: StructuredTableColumn[]` (shape v1).
 *
 * El shape v2 (Doc 24 §2.6) movió ese schema a
 * `meta.table_config.columns: TableColumn[]` con:
 *   · `type: 'plain_text' | 'enum' | 'number' | 'date'`
 *   · `enum_values: { value, i18n.{lang}.label }[]` (en lugar de
 *     `options: string[]`)
 *   · `i18n.{lang}.{label, placeholder?}` por columna.
 *
 * Este helper normaliza ambos shapes a un único `ColumnLite[]` que el
 * editor / viewer consumen. Además deriva `columnLabels[key] = label`
 * para el locale activo, sustituyendo el legacy `i18nSection.column_labels`
 * (mapa plano que solo existía en v1).
 *
 * Reglas de mapping v2 → lite:
 *   · `type='plain_text'` → `type='text'` (el renderer no conoce
 *     plain_text como caso explícito; cae al else=text que es correcto).
 *   · `enum.options` = lista de `value` snake_case estables (no labels).
 *     El runtime guarda el `value` y el renderer mapea a label en
 *     visualización si quiere — para edición se ve el value mismo,
 *     consistente con el contrato actual del editor.
 *   · `columnLabels[key]` = `col.i18n[locale].label`, con fallback
 *     `es → en → primer disponible`.
 *
 * Cuando llega shape v1 (legacy / tests viejos), retorna `meta.columns`
 * intacto y `columnLabels` undefined (el caller usa
 * `i18nSection.column_labels` por su cuenta).
 */

import type {
  StructuredTableColumn,
  TableColumn,
  TableConfig,
} from '../api/templates'
import { localeFallbackOrder } from '../i18n/config'

/** Shape lite que consume el editor / renderer (compatible v1). */
export interface ColumnLite {
  key: string
  type: string
  required?: boolean
  options?: string[]
  prefix?: string
  pad?: number
  /** Doc 24 v2 — etiqueta localizada del header de la columna. El
   * editor/viewer usa este label en vez de `key.toUpperCase()`. Solo se
   * llena cuando el meta viene en shape v2 con `table_config`. En v1
   * legacy, los labels viven en `i18nSection.column_labels` que el
   * caller maneja por su cuenta. */
  label?: string
  /** Doc 24 v2 — mapa `{value: label}` para enum_values. El dropdown
   * muestra `optionLabels[opt] ?? opt`; el value persistido sigue siendo
   * el `value` snake_case estable (no se traduce). */
  optionLabels?: Record<string, string>
  /** Doc 19 §10 — solo `type === 'currency'`. ISO 4217 que pre-selecciona
   * el dropdown al crear nueva fila. El render del canon lo usa como
   * fallback cuando la celda omite `code`. */
  defaultCurrency?: string
  /** Doc 19 §10 — solo `type === 'currency'`. Lista opcional de
   * códigos ISO 4217 que restringen el dropdown del editor. */
  allowedCurrencies?: string[]
}

export interface NormalizedTableSchema {
  columns: ColumnLite[]
  /** `{key: label}` para el locale activo. `undefined` cuando llega v1
   * legacy (el caller cae al `i18nSection.column_labels` legacy). */
  columnLabels?: Record<string, string>
}

interface MetaShape {
  table_config?: TableConfig | null
  columns?: StructuredTableColumn[]
  [extra: string]: unknown
}

/** Devuelve el shape lite que el editor/renderer espera, detectando
 * automáticamente si el meta viene en v2 (`table_config`) o v1
 * (`columns` legacy). `locale` se usa solo para v2 (deriva labels). */
export function normalizeTableSchema(
  meta: MetaShape | null | undefined,
  locale: string = 'es',
): NormalizedTableSchema {
  if (!meta) return { columns: [] }

  const tableConfig = meta.table_config
  if (
    tableConfig &&
    typeof tableConfig === 'object' &&
    Array.isArray(tableConfig.columns)
  ) {
    // Shape v2.
    const columns: ColumnLite[] = []
    const labels: Record<string, string> = {}
    for (const col of tableConfig.columns as TableColumn[]) {
      if (!col || typeof col !== 'object') continue
      const key = String(col.key || '').trim()
      if (!key) continue

      const lite: ColumnLite = {
        key,
        type: col.type === 'plain_text' ? 'text' : col.type,
        required: col.required ?? false,
      }
      if (col.type === 'currency') {
        // Doc 19 §10 — campos exclusivos del tipo currency. El render
        // y el editor los necesitan para el dropdown de moneda y el
        // fallback cuando una celda no declara `code`.
        if (col.default_currency) {
          lite.defaultCurrency = col.default_currency
        }
        if (Array.isArray(col.allowed_currencies) && col.allowed_currencies.length > 0) {
          lite.allowedCurrencies = [...col.allowed_currencies]
        }
      }
      if (col.type === 'autonum') {
        // Doc 06 §Backlog — campos exclusivos del tipo autonum. El
        // render (`formatStructuredCell` caso 'autonum') usa estos
        // valores para calcular el ID: `${prefix}${pad-padded ordinal}`.
        if (typeof col.prefix === 'string') lite.prefix = col.prefix
        if (typeof col.pad === 'number') lite.pad = col.pad
      }
      if (col.type === 'enum') {
        const evs = Array.isArray(col.enum_values) ? col.enum_values : []
        const options: string[] = []
        const optionLabels: Record<string, string> = {}
        for (const ev of evs) {
          if (!ev || typeof ev !== 'object') continue
          const v = String(ev.value || '').trim()
          const evI18n = ev.i18n ?? {}
          // Label localizado del enum value (fallback es → en → primer).
          let evLabel = ''
          for (const candidate of localeFallbackOrder(locale)) {
            const block = evI18n[candidate]
            const lbl = block ? String(block.label || '').trim() : ''
            if (lbl) {
              evLabel = lbl
              break
            }
          }
          if (!evLabel) {
            for (const block of Object.values(evI18n)) {
              if (!block) continue
              const lbl = String(block.label || '').trim()
              if (lbl) {
                evLabel = lbl
                break
              }
            }
          }
          if (v) {
            options.push(v)
            if (evLabel) optionLabels[v] = evLabel
          } else if (evLabel) {
            // Fallback raro: sin value pero con label.
            options.push(evLabel)
          }
        }
        lite.options = options
        if (Object.keys(optionLabels).length > 0) {
          lite.optionLabels = optionLabels
        }
      }
      columns.push(lite)

      // Label per locale con fallback es → en → primer disponible.
      const colI18n = col.i18n ?? {}
      let label = ''
      for (const candidate of localeFallbackOrder(locale)) {
        const block = colI18n[candidate]
        const lbl = block ? String(block.label || '').trim() : ''
        if (lbl) {
          label = lbl
          break
        }
      }
      if (!label) {
        for (const block of Object.values(colI18n)) {
          if (!block) continue
          const lbl = String(block.label || '').trim()
          if (lbl) {
            label = lbl
            break
          }
        }
      }
      if (label) {
        labels[key] = label
        // También en la columna para que CellEditor lo encuentre sin
        // necesidad de pasarle el map por separado.
        lite.label = label
      }
    }
    return { columns, columnLabels: labels }
  }

  // Shape v1 legacy.
  const legacy = Array.isArray(meta.columns) ? meta.columns : []
  const v1Cols: ColumnLite[] = legacy.map((c) => {
    const lite: ColumnLite = {
      key: c.key,
      type: c.type,
    }
    if (typeof c.required === 'boolean') lite.required = c.required
    if (c.options) lite.options = c.options
    if (c.prefix) lite.prefix = c.prefix
    if (typeof c.pad === 'number') lite.pad = c.pad
    return lite
  })
  return { columns: v1Cols }
}
